import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

const API = process.env.REACT_APP_API_URL || "";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const STATUS_CONFIG = {
  scheduled:  { label: "Scheduled",  color: "#1D4ED8", bg: "#DBEAFE" },
  completed:  { label: "Completed",  color: "#065F46", bg: "#D1FAE5" },
  cancelled:  { label: "Cancelled",  color: "#6B7280", bg: "#F3F4F6" },
  "no-show":  { label: "No Show",    color: "#92400E", bg: "#FEF3C7" },
};

function fmt12(time24) {
  if (!time24) return "—";
  const [h, m] = time24.split(":").map(Number);
  const p = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${p}`;
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function timeAgo(str) {
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const DEFAULT_DAYS = DAY_NAMES.map((_, i) => ({
  dayIndex: i, isOpen: i > 0 && i < 6, startTime: "09:00", endTime: "17:00",
}));

// ── Availability Settings Panel ───────────────────────────────────────
function AvailabilityPanel() {
  const [days,         setDays]         = useState(DEFAULT_DAYS);
  const [slotDuration, setSlotDuration] = useState(30);
  const [blockedInput, setBlockedInput] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);
  const [saving, setSaving]   = useState(false);
  const [saved,  setSaved]    = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminFetch(`${API}/api/appointments/admin/settings`)
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setDays(j.data.days || DEFAULT_DAYS);
          setSlotDuration(j.data.slotDuration || 30);
          setBlockedDates(j.data.blockedDates || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (i) => setDays(prev => prev.map(d => d.dayIndex === i ? { ...d, isOpen: !d.isOpen } : d));
  const updateDay = (i, field, val) => setDays(prev => prev.map(d => d.dayIndex === i ? { ...d, [field]: val } : d));

  const addBlocked = () => {
    const v = blockedInput.trim();
    if (!v || blockedDates.includes(v)) return;
    setBlockedDates(prev => [...prev, v].sort());
    setBlockedInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch(`${API}/api/appointments/admin/settings`, {
        method: "PUT",
        body: JSON.stringify({ slotDuration, days, blockedDates }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (_) {}
    setSaving(false);
  };

  if (loading) return <div style={{ padding: "32px", textAlign: "center", color: "rgba(1,46,114,0.4)", fontFamily: "'Constantia', serif", fontSize: "14px" }}>Loading settings…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      {/* Slot duration */}
      <div>
        <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "12px" }}>Appointment Duration</p>
        <div style={{ display: "flex", gap: "10px" }}>
          {[15, 30, 45, 60].map(d => (
            <button key={d} onClick={() => setSlotDuration(d)} style={{
              padding: "9px 20px", borderRadius: "999px", cursor: "pointer", transition: "all 0.15s",
              fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.12em",
              background: slotDuration === d ? "#012E72" : "#fff",
              color:      slotDuration === d ? "#fff"    : "#6B7280",
              border: `1px solid ${slotDuration === d ? "#012E72" : "rgba(1,46,114,0.12)"}`,
            }}>{d} min</button>
          ))}
        </div>
      </div>

      {/* Weekly schedule */}
      <div>
        <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "12px" }}>Weekly Schedule</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {days.map(d => (
            <div key={d.dayIndex} style={{ display: "flex", alignItems: "center", gap: "16px", padding: "14px 18px", background: "#fff", borderRadius: "12px", border: "1px solid rgba(1,46,114,0.09)" }}>
              {/* Toggle */}
              <button
                onClick={() => toggleDay(d.dayIndex)}
                style={{ width: "40px", height: "22px", borderRadius: "999px", border: "none", cursor: "pointer", background: d.isOpen ? "#012E72" : "rgba(1,46,114,0.15)", transition: "all 0.2s", position: "relative", flexShrink: 0 }}
              >
                <span style={{ position: "absolute", top: "3px", left: d.isOpen ? "21px" : "3px", width: "16px", height: "16px", borderRadius: "50%", background: "#fff", transition: "all 0.2s" }} />
              </button>
              {/* Day name */}
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.1em", color: d.isOpen ? "#012E72" : "rgba(1,46,114,0.3)", width: "90px", flexShrink: 0 }}>
                {DAY_NAMES[d.dayIndex]}
              </span>
              {/* Time range */}
              {d.isOpen ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input type="time" value={d.startTime} onChange={e => updateDay(d.dayIndex, "startTime", e.target.value)}
                    style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "8px", padding: "6px 10px", outline: "none" }} />
                  <span style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", color: "rgba(1,46,114,0.4)" }}>to</span>
                  <input type="time" value={d.endTime} onChange={e => updateDay(d.dayIndex, "endTime", e.target.value)}
                    style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "8px", padding: "6px 10px", outline: "none" }} />
                </div>
              ) : (
                <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.3)", fontStyle: "italic" }}>Closed</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Blocked dates */}
      <div>
        <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "12px" }}>Blocked Dates (Holidays / Closures)</p>
        <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
          <input type="date" value={blockedInput} onChange={e => setBlockedInput(e.target.value)}
            style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "10px", padding: "9px 14px", outline: "none", flex: 1 }} />
          <button onClick={addBlocked} style={{ padding: "9px 20px", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#fff", background: "#012E72", border: "none", borderRadius: "10px", cursor: "pointer" }}>Block</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {blockedDates.map(d => (
            <span key={d} style={{ display: "flex", alignItems: "center", gap: "6px", background: "#FEF3C7", color: "#92400E", borderRadius: "999px", padding: "5px 12px", fontFamily: "'Constantia', serif", fontSize: "12px" }}>
              {new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              <button onClick={() => setBlockedDates(prev => prev.filter(x => x !== d))}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#92400E", fontSize: "14px", lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      </div>

      {/* Save */}
      <button onClick={save} disabled={saving} style={{
        alignSelf: "flex-start", padding: "12px 32px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase",
        color: "#fff", background: saved ? "linear-gradient(135deg,#065F46,#059669)" : "linear-gradient(135deg,#012E72,#002DB5)",
        border: "none", borderRadius: "12px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, transition: "all 0.3s",
        boxShadow: "0 4px 16px rgba(0,45,181,0.25)",
      }}>
        {saving ? "Saving…" : saved ? "✓ Saved" : "Save Availability"}
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function AdminSchedule() {
  usePageTitle("Appointments");
  const navigate = useNavigate();
  const [tab,    setTab]    = useState("appointments"); // appointments | availability
  const [items,  setItems]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [total, setTotal]   = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res  = await adminFetch(`${API}/api/appointments/admin/list?${params}`);
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      const json = await res.json();
      if (json.success) { setItems(json.data); setTotal(json.total); }
    } catch (_) {}
    setLoading(false);
  }, [navigate, statusFilter]);

  useEffect(() => { if (tab === "appointments") load(); }, [tab, load]);

  const updateStatus = async (id, status) => {
    try {
      await adminFetch(`${API}/api/appointments/admin/${id}`, {
        method: "PATCH", body: JSON.stringify({ status }),
      });
      setItems(prev => prev.map(a => a._id === id ? { ...a, status } : a));
      setSelected(prev => prev?._id === id ? { ...prev, status } : prev);
    } catch (_) {}
  };

  const deleteAppt = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await adminFetch(`${API}/api/appointments/admin/${id}`, { method: "DELETE" });
      setItems(prev => prev.filter(a => a._id !== id));
      setSelected(null);
    } catch (_) {}
  };

  const counts = items.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px" }}>Admin / Schedule</p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>Appointments</h1>
        </div>
        <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)" }}>{total} total</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", background: "rgba(1,46,114,0.05)", padding: "4px", borderRadius: "12px", alignSelf: "flex-start" }}>
        {[{ key: "appointments", label: "Appointments" }, { key: "availability", label: "Availability Settings" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "9px 20px", borderRadius: "9px", border: "none", cursor: "pointer", transition: "all 0.15s",
            fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase",
            background: tab === t.key ? "#fff" : "transparent",
            color:      tab === t.key ? "#012E72" : "rgba(1,46,114,0.45)",
            boxShadow:  tab === t.key ? "0 2px 8px rgba(1,46,114,0.1)" : "none",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Appointments Tab */}
      {tab === "appointments" && (
        <>
          {/* Stat pills */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {[
              { label: "Total",     value: total,                   color: "#012E72", bg: "rgba(1,46,114,0.07)" },
              { label: "Scheduled", value: counts.scheduled || 0,   color: "#1D4ED8", bg: "#DBEAFE" },
              { label: "Completed", value: counts.completed || 0,   color: "#065F46", bg: "#D1FAE5" },
              { label: "Cancelled", value: counts.cancelled || 0,   color: "#6B7280", bg: "#F3F4F6" },
            ].map(s => (
              <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: "10px", padding: "10px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: s.color }}>{s.value}</span>
                <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em", textTransform: "uppercase", color: s.color, opacity: 0.7 }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Status filter */}
          <div style={{ display: "flex", gap: "6px" }}>
            {["all", "scheduled", "completed", "cancelled", "no-show"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                padding: "8px 16px", borderRadius: "999px", cursor: "pointer", transition: "all 0.15s",
                fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase",
                background: statusFilter === s ? "#012E72" : "#fff",
                color:      statusFilter === s ? "#fff"    : "#6B7280",
                border: `1px solid ${statusFilter === s ? "#012E72" : "rgba(1,46,114,0.12)"}`,
              }}>
                {s === "all" ? "All" : STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>

          {/* Table */}
          <div style={{ background: "#fff", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 16px rgba(1,46,114,0.06)" }}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: "64px 32px", textAlign: "center" }}>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#012E72", marginBottom: "8px" }}>No appointments yet</p>
                <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>Bookings will appear here once clients schedule via the website.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                      {["Client", "Email", "Date", "Time", "Topic", "Booked", "Status", ""].map((h, i) => (
                        <th key={i} style={{ padding: "13px 18px", textAlign: "left", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", backgroundColor: "rgba(1,46,114,0.025)", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a, i) => {
                      const scfg = STATUS_CONFIG[a.status] || {};
                      return (
                        <tr key={a._id} onClick={() => setSelected(a)} style={{ borderBottom: i < items.length - 1 ? "1px solid rgba(1,46,114,0.055)" : "none", cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(1,46,114,0.025)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "14px 18px", fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "13px", color: "#012E72", whiteSpace: "nowrap" }}>{a.fullName}</td>
                          <td style={{ padding: "14px 18px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.email}</td>
                          <td style={{ padding: "14px 18px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", whiteSpace: "nowrap" }}>{fmtDate(a.date)}</td>
                          <td style={{ padding: "14px 18px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", whiteSpace: "nowrap" }}>{fmt12(a.time)}</td>
                          <td style={{ padding: "14px 18px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.topic || "—"}</td>
                          <td style={{ padding: "14px 18px", fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.38)", whiteSpace: "nowrap" }}>{timeAgo(a.createdAt)}</td>
                          <td style={{ padding: "14px 18px" }}>
                            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: scfg.color, background: scfg.bg, borderRadius: "6px", padding: "4px 10px", whiteSpace: "nowrap" }}>{scfg.label}</span>
                          </td>
                          <td style={{ padding: "14px 14px" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.25)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Detail modal */}
          {selected && (
            <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", backgroundColor: "rgba(1,4,20,0.5)", backdropFilter: "blur(4px)" }}>
              <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "520px", background: "#F7F4EF", borderRadius: "20px", overflow: "hidden", boxShadow: "0 32px 80px rgba(1,46,114,0.22)", border: "1px solid rgba(1,46,114,0.1)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <div style={{ background: "linear-gradient(135deg,#012E72,#002DB5)", padding: "22px 28px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexShrink: 0 }}>
                  <div>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: "6px" }}>Appointment</p>
                    <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "0.04em" }}>{selected.fullName}</h2>
                    <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(255,255,255,0.55)", marginTop: "4px" }}>{fmtDate(selected.date)} · {fmt12(selected.time)}</p>
                  </div>
                  <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.45)" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#fff"} onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>

                {/* Body */}
                <div style={{ padding: "24px 28px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "20px" }}>
                  {/* Contact info */}
                  <div>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "12px" }}>Contact</p>
                    {[["Email", selected.email, `mailto:${selected.email}`], ["Phone", selected.phone, `tel:${selected.phone}`]].filter(([,v]) => v).map(([l, v, h]) => (
                      <div key={l} style={{ display: "flex", gap: "14px", padding: "10px 0", borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", width: "64px", flexShrink: 0 }}>{l}</span>
                        <a href={h} style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#002DB5", textDecoration: "none" }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"} onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>{v}</a>
                      </div>
                    ))}
                    {selected.topic && (
                      <div style={{ display: "flex", gap: "14px", padding: "10px 0", borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", width: "64px", flexShrink: 0 }}>Topic</span>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72" }}>{selected.topic}</span>
                      </div>
                    )}
                    {selected.notes && (
                      <div style={{ display: "flex", gap: "14px", padding: "10px 0" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", width: "64px", flexShrink: 0 }}>Notes</span>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72", lineHeight: 1.6 }}>{selected.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Status change */}
                  <div>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>Status</p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {Object.entries(STATUS_CONFIG).map(([s, cfg]) => {
                        const active = selected.status === s;
                        return (
                          <button key={s} onClick={() => updateStatus(selected._id, s)} style={{
                            fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase",
                            border: "none", borderRadius: "8px", padding: "9px 18px", cursor: "pointer",
                            background: active ? cfg.bg    : "rgba(1,46,114,0.05)",
                            color:      active ? cfg.color : "rgba(1,46,114,0.35)",
                            fontWeight: active ? 700 : 400,
                            outline:    active ? `2px solid ${cfg.color}30` : "none", outlineOffset: "2px",
                          }}>{cfg.label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{ padding: "16px 28px", borderTop: "1px solid rgba(1,46,114,0.08)", background: "rgba(1,46,114,0.02)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <button onClick={() => deleteAppt(selected._id)} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#DC2626", border: "1px solid rgba(220,38,38,0.22)", borderRadius: "999px", padding: "8px 18px", cursor: "pointer", background: "transparent" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#DC2626"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#DC2626"; }}>Delete</button>
                  <button onClick={() => setSelected(null)} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg,#012E72,#002DB5)", border: "none", borderRadius: "999px", padding: "9px 24px", cursor: "pointer" }}>Close</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Availability Tab */}
      {tab === "availability" && (
        <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 16px rgba(1,46,114,0.06)" }}>
          <AvailabilityPanel />
        </div>
      )}
    </div>
  );
}
